import React, { useMemo, useRef } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import handGood from "../../assets/hand-good.png";
import handWrong from "../../assets/hand-wrong.png";
import historicalTable from "../../assets/historico-analiticas.png";
import logoMedical from "../../assets/logo-medical.png";
import mano from "../../assets/mano.png";
import reset from "../../assets/reset.png";
import { ROUTES } from "../../utils/constants";
import { getScenarioByScreen, handleNavigate } from "../../utils/helpers";
import { modalGeneric, modalQuestionInfo } from "../../utils/logger";
import { SERVER_URL } from "../../api";
import { useGameTexts } from "../../hooks/useGameTexts";

function buildAvatarUrl(value) {
	if (!value || typeof value !== "string") return null;
	if (value.startsWith("http")) return value;
	return `${SERVER_URL.replace(/\/$/, "")}/${value.replace(/^\//, "")}`;
}

function parseConversationMessages(value) {
	if (!value || typeof value !== "string") return null;
	try {
		const arr = JSON.parse(value);
		return Array.isArray(arr) && arr.length > 0
			? arr.filter((m) => m && (m.speaker === "doctor" || m.speaker === "doctora" || m.speaker === "patient" || m.speaker === "prompt"))
			: null;
	} catch {
		return null;
	}
}

/** Reemplaza placeholders del backoffice por img tags con los assets */
function injectModalPlaceholders(html, assets) {
	if (!html || typeof html !== "string") return html;
	let out = html;
	if (assets.historicalTable) out = out.replace("{{HISTORICAL_TABLE_IMAGE}}", `<img width="100%" src="${assets.historicalTable}" alt="" />`);
	if (assets.mano) out = out.replace("{{MANO}}", `<img width="10%" src="${assets.mano}" alt="" />`);
	if (assets.handGood) out = out.replace("{{HAND_GOOD}}", `<img width="25" src="${assets.handGood}" class="me-1" alt="" />`);
	if (assets.handWrong) out = out.replace("{{HAND_WRONG}}", `<img width="20" src="${assets.handWrong}" class="me-2" alt="" />`);
	return out;
}

/** Encuentra bloques <figure>...</figure> por posición (evita fallos por espacios/cambios de línea). */
function findFigureBlocks(html) {
	const blocks = [];
	let i = 0;
	while (i < html.length) {
		const start = html.indexOf("<figure", i);
		if (start === -1) break;
		const openEnd = html.indexOf(">", start);
		if (openEnd === -1) break;
		const closeStart = html.indexOf("</figure>", openEnd + 1);
		if (closeStart === -1) break;
		const end = closeStart + "</figure>".length;
		const full = html.slice(start, end);
		const content = html.slice(openEnd + 1, closeStart);
		blocks.push({ start, end, full, content });
		i = end;
	}
	return blocks;
}

/** Envuelve tablas del histórico para aplicar gradiente a fechas y estilo a datos. Soporta: dos <figure>, dos <table> sueltas, o una sola tabla con thead/tbody. */
function repairHistoricoHtml(html) {
	if (!html || typeof html !== "string") return html;
	if (html.includes("historico-dates-wrapper")) return html;

	const figures = findFigureBlocks(html);
	const hasTable = /<table[\s>]/i.test(html);
	const hasTh = /<th[\s>]/i.test(html);

	// Dos figuras (o una): envolver por contenido th = fechas, td = datos. Inline styles para que el modal no los pise.
	const datesWrapperStyle = "margin-top:1rem;background:linear-gradient(90deg,#c5dde8 0%,#006292 60%);border-radius:10px 10px 0 0;border:1px solid #dee2e6;border-bottom:none;overflow:hidden;";
	const thStyle = "color:#fff!important;background:transparent!important;font-weight:700;border:none;border-right:1px solid rgba(255,255,255,0.35);padding:8px 10px;text-align:center;font-size:14px;box-sizing:border-box;";
	if (figures.length >= 1) {
		const parts = [];
		let lastEnd = 0;
		figures.forEach((fig) => {
			parts.push(html.slice(lastEnd, fig.start));
			const isDates = /<th[\s>]/.test(fig.content);
			const wrapperClass = isDates ? "historico-dates-wrapper" : "historico-data-wrapper";
			if (isDates) {
				let inner = fig.full
					.replace(/<figure(\s[^>]*)?>/i, "<figure style=\"margin:0;padding:0;background:transparent!important\">")
					.replace(/<table(\s[^>]*)?>/i, "<table style=\"width:100%;border-collapse:collapse;background:transparent!important;table-layout:fixed\">");
				let thCount = 0;
				inner = inner.replace(/<th(\s[^>]*)?>/gi, () => {
					thCount++;
					const firstCell = thCount === 1;
					const color = firstCell ? "color:transparent!important;" : "color:#fff!important;";
					return `<th style="${color}background:transparent!important;font-weight:700;border:none;border-right:1px solid rgba(255,255,255,0.35);padding:8px 10px;text-align:center;font-size:14px;box-sizing:border-box;">`;
				});
				parts.push(`<div class="${wrapperClass}" style="${datesWrapperStyle}">${inner}</div>`);
			} else {
				parts.push(`<div class="${wrapperClass}">${fig.full}</div>`);
			}
			lastEnd = fig.end;
		});
		parts.push(html.slice(lastEnd));
		return parts.join("");
	}

	// Sin figure pero con tablas: buscar <table> por posición
	if (hasTable && !figures.length) {
		const tableRegex = /<table(\s[^>]*)?>/gi;
		const tables = [];
		let m;
		while ((m = tableRegex.exec(html)) !== null) {
			tables.push({ openStart: m.index, openEnd: m.index + m[0].length, attrs: (m[1] || "").trim() });
		}
		if (tables.length >= 2) {
			const closeRegex = /<\/table>/gi;
			const closes = [];
			while ((m = closeRegex.exec(html)) !== null) closes.push(m.index);
			if (closes.length >= 2) {
				const firstTableContent = html.slice(tables[0].openEnd, closes[0]);
				const secondTableContent = html.slice(tables[1].openEnd, closes[1]);
				const firstIsDates = /<th[\s>]/.test(firstTableContent);
				const secondIsDates = /<th[\s>]/.test(secondTableContent);
				let out = html;
				const wrap1 = firstIsDates ? "historico-dates-wrapper" : "historico-data-wrapper";
				const wrap2 = secondIsDates ? "historico-dates-wrapper" : "historico-data-wrapper";
				const class1 = firstIsDates ? "historico-dates-table" : "historico-data-table";
				const class2 = secondIsDates ? "historico-dates-table" : "historico-data-table";
				out = out.slice(0, tables[0].openStart) + `<div class="${wrap1}"><table class="${class1}"${tables[0].attrs ? " " + tables[0].attrs : ""}>` + out.slice(tables[0].openEnd, closes[0]) + "</table></div>" + out.slice(closes[0], tables[1].openStart) + `<div class="${wrap2}"><table class="${class2}"${tables[1].attrs ? " " + tables[1].attrs : ""}>` + out.slice(tables[1].openEnd, closes[1]) + "</table></div>" + out.slice(closes[1]);
				return out;
			}
		}
		if (tables.length === 1 && hasTh) {
			// Una sola tabla con th (thead o primera fila): wrapper único y CSS aplica a th y td
			const t = tables[0];
			const closeIdx = html.indexOf("</table>", t.openEnd);
			if (closeIdx !== -1) {
				const before = html.slice(0, t.openStart);
				const after = html.slice(closeIdx + "</table>".length);
				const tableContent = html.slice(t.openEnd, closeIdx);
				const attrs = (t.attrs ? " " + t.attrs : "");
				return before + `<div class="historico-single-table-wrapper"><table class="historico-dates-table"${attrs}>${tableContent}</table></div>` + after;
			}
		}
	}

	return html;
}

/** Estilos del histórico inyectados en el modal. Incluye reglas para estructura con <figure class="table"> (editor) para no pisar colores inline. */
function getHistoricoModalStyles() {
	return `<style>
.historico-modal-content .historico-dates-wrapper { margin-top:1rem; background:linear-gradient(90deg,#c5dde8 0%,#006292 60%); border-radius:10px 10px 0 0; border:1px solid #dee2e6; border-bottom:none; overflow:hidden; }
.historico-modal-content .historico-dates-wrapper figure { margin:0; padding:0; background:transparent; }
.historico-modal-content .historico-dates-table { width:100%; border-collapse:collapse; table-layout:fixed; }
.historico-modal-content .historico-dates-table th { background:transparent !important; color:#fff; font-weight:700; border:none; border-right:1px solid rgba(255,255,255,0.35); padding:8px 10px; text-align:center; font-size:14px; box-sizing:border-box; }
.historico-modal-content .historico-dates-table th:last-child { border-right:none; }
.historico-modal-content .historico-dates-table th:first-child { text-align:left; width:18%; min-width:0; color:transparent; }
.historico-modal-content .historico-dates-wrapper table { width:100%; border-collapse:collapse; table-layout:fixed; background:transparent !important; }
.historico-modal-content .historico-dates-wrapper table th { background:transparent !important; color:#fff !important; font-weight:700; border:none; border-right:1px solid rgba(255,255,255,0.35); padding:8px 10px; text-align:center; font-size:14px; box-sizing:border-box; }
.historico-modal-content .historico-dates-wrapper table th:last-child { border-right:none; }
.historico-modal-content .historico-dates-wrapper table th:first-child { text-align:left; width:18%; min-width:0; color:transparent !important; }
.historico-modal-content .historico-dates-wrapper table tbody tr:first-child th { background:transparent !important; color:#fff !important; }
.historico-modal-content .historico-single-table-wrapper { margin-top:1rem; border:1px solid #dee2e6; border-radius:10px; overflow:hidden; }
.historico-modal-content .historico-single-table-wrapper table { width:100%; border-collapse:collapse; table-layout:fixed; font-size:14px; }
.historico-modal-content .historico-single-table-wrapper thead { display:table-header-group; }
.historico-modal-content .historico-single-table-wrapper thead tr { background:linear-gradient(90deg,#c5dde8 0%,#006292 60%); }
.historico-modal-content .historico-single-table-wrapper thead th { background:transparent !important; color:#fff; font-weight:700; border:none; border-right:1px solid rgba(255,255,255,0.35); padding:8px 10px; text-align:center; font-size:14px; box-sizing:border-box; }
.historico-modal-content .historico-single-table-wrapper thead th:last-child { border-right:none; }
.historico-modal-content .historico-single-table-wrapper thead th:first-child { text-align:left; width:18%; min-width:0; color:transparent; }
.historico-modal-content .historico-single-table-wrapper tbody tr:first-child { background:linear-gradient(90deg,#c5dde8 0%,#006292 60%); }
.historico-modal-content .historico-single-table-wrapper tbody tr:first-child th { background:transparent !important; color:#fff !important; border:none; border-right:1px solid rgba(255,255,255,0.35); padding:8px 10px; text-align:center; font-size:14px; }
.historico-modal-content .historico-single-table-wrapper tbody tr:first-child th:first-child { color:transparent !important; }
.historico-modal-content .historico-single-table-wrapper tbody td { border:1px solid #dee2e6; padding:8px 10px; text-align:center; background:#fff; box-sizing:border-box; }
.historico-modal-content .historico-single-table-wrapper tbody td:first-child { text-align:left; width:18%; min-width:0; font-weight:700 !important; background:#fff; }
.historico-modal-content .historico-single-table-wrapper tbody tr:first-child td { border-top:none; }
.historico-modal-content .historico-data-wrapper { border:1px solid #dee2e6; border-radius:0 0 10px 10px; overflow:hidden; }
.historico-modal-content .historico-data-table { width:100%; border-collapse:collapse; font-size:14px; table-layout:fixed; }
.historico-modal-content .historico-data-table td { border:1px solid #dee2e6; padding:8px 10px; text-align:center; background:#fff; color:#000; box-sizing:border-box; }
.historico-modal-content .historico-data-table td:first-child { text-align:left; width:18%; min-width:0; font-weight:700 !important; background:#fff; }
.historico-modal-content .historico-data-wrapper table { width:100%; border-collapse:collapse; font-size:14px; table-layout:fixed; }
.historico-modal-content .historico-data-wrapper table td { border:1px solid #dee2e6; padding:8px 10px; text-align:center; background:#fff; box-sizing:border-box; }
.historico-modal-content .historico-data-wrapper table td:first-child { text-align:left; width:18%; min-width:0; font-weight:700 !important; background:#fff; }
.historico-modal-content .historico-data-table .cell-highlight { color:#dc3545; font-weight:700 !important; }
.historico-modal-content .historico-data-table .cell-bold { font-weight:700 !important; }
.historico-modal-content .historico-modal-block .historico-modal-intro { color:#00677c; font-size:20px; font-weight:400; text-align:left; }
.historico-modal-content .historico-modal-block .historico-modal-subtitle { color:#6c757d; font-size:16px; margin-top:0.5rem; text-align:left; }
.historico-modal-content .historico-modal-block .historico-modal-title { color:#00677c; font-weight:700; font-size:18px; }
.historico-modal-content .historico-modal-block .historico-modal-patient { color:#00677c; font-weight:400; font-size:16px; }
.historico-modal-content p { margin-bottom:0.5rem; }
.historico-modal-content > p:first-of-type { color:#00677c; font-size:20px; font-weight:400; }
.historico-modal-content > p:nth-of-type(2) { color:#6c757d; font-size:16px; }
.historico-modal-content > p:nth-of-type(3) { color:#00677c; font-weight:700; font-size:18px; }
</style>`;
}

const EXPLORATION_HTML = `<div class="d-flex flex-column align-items-start justify-content-center">
<div class="txt-turquoise txt20">
	<strong>Realizas una exploración física a la paciente.</strong>
</div>
<div class="my-3">Estos son los resultados:</div>
<ul>
	<li>Altura: 165 cm, peso: 61 kg</li>
	<li>PA en consulta externa: 142/92 mmHg</li>
	<li>FC: 78 lpm </li>
	<li>
		Afebril, buen estado general. Consciente y orientada,
		colaboradora. Normocoloreada y normohidratada.
	</li>
	<li>Torácica cerrada y resonante, sin soplos.</li>
	<li>Normoperfundida, edema ++ pretibial.</li>
	<li>No lesiones cutáneas.</li>
	<li>
		Eupneica en reposo, movimientos coordinados voluntarios, sin
		ruidos sobreañadidos.
	</li>
	<li>
		Sin focalidad neurológica ni <i>flapping</i>.
	</li>
</ul>
</div>`;

const HISTORICAL_TABLE_HTML = `<div class="historico-modal-block d-flex flex-column align-items-center justify-content-center text-start">
<div class="historico-modal-intro w-100">
	Finalmente, entre la documentación observas que hay un histórico
	de análisis.
</div>
<div class="historico-modal-subtitle w-100">Échale un vistazo por si acaso…</div>
<div class="w-100 mt-3 text-start">
	<div class="d-flex justify-content-between align-items-center mb-2">
		<span class="historico-modal-title">Histórico de analíticas</span>
		<span class="historico-modal-patient">Paciente: Martina Ferrater</span>
	</div>
	<div class="historico-dates-wrapper">
		<table class="historico-dates-table">
			<tr>
				<th></th>
				<th>09-06-2016</th>
				<th>10-07-2018</th>
				<th>14-10-2021</th>
				<th>08-09-2022</th>
				<th>04-10-2023</th>
			</tr>
		</table>
	</div>
	<div class="historico-data-wrapper">
		<table class="historico-data-table">
		<tbody>
			<tr>
				<td>Hb (g/dl)</td>
				<td>13,7</td>
				<td>12,8</td>
				<td class="cell-highlight">11,9</td>
				<td class="cell-highlight">11</td>
				<td class="cell-highlight">10,9</td>
			</tr>
			<tr>
				<td>Leucocitos (/µL)</td>
				<td>6715</td>
				<td>7290</td>
				<td class="cell-highlight">3210</td>
				<td class="cell-highlight">2413</td>
				<td class="cell-highlight">2050</td>
			</tr>
			<tr>
				<td>Plaquetas (/µL)</td>
				<td>218 000</td>
				<td>197 000</td>
				<td>192 000</td>
				<td>183 000</td>
				<td>178 000</td>
			</tr>
			<tr>
				<td>Creatinina (mg/dl)</td>
				<td>0,81</td>
				<td>0,87</td>
				<td>0,79</td>
				<td>0,8</td>
				<td>0,71</td>
			</tr>
			<tr>
				<td>Leucocitos orina</td>
				<td>0</td>
				<td>0</td>
				<td>0</td>
				<td>0</td>
				<td class="cell-highlight">10-15/c</td>
			</tr>
			<tr>
				<td>Hematíes orina</td>
				<td>0</td>
				<td>1-5/c</td>
				<td>0</td>
				<td>0</td>
				<td class="cell-highlight">15-20/c</td>
			</tr>
			<tr>
				<td>UPCR (mg/g)</td>
				<td>12,3</td>
				<td>46</td>
				<td>150</td>
				<td>345</td>
				<td class="cell-highlight">3700</td>
			</tr>
			<tr>
				<td>ANA</td>
				<td></td>
				<td class="cell-highlight">Positivo</td>
				<td></td>
				<td></td>
				<td></td>
			</tr>
			<tr>
				<td>Factor reumatoide</td>
				<td></td>
				<td class="cell-bold">Negativo</td>
				<td></td>
				<td></td>
				<td></td>
			</tr>
		</tbody>
		</table>
	</div>
</div>
</div>`;

// Parte editable (título + instrucciones). El bloque de puntuación (+10/-20) es fijo y no se edita en backoffice.
const QUESTION_INFO_EDITABLE = `<div class="d-flex flex-column align-items-start justify-content-center">
<div class="txt-turquoise bold txt20 text-center">
	Tras revisar toda la información relacionada con esta paciente,
	¿qué pruebas solicitarías?
</div>
<div class="d-flex align-items-start mt-4">
	<img width="10%" src=${mano} />
	<ul>
		<li>
			En la siguiente batería de pruebas, hay 3 que no se
			justifican, y 6 que pueden ser justificables. Selecciona
			solo las 6 pruebas justificables y lee con atención los
			resultados.
		</li>
		<li class="mt-2">
			Avanzarás cuando hayas seleccionado las 6 pruebas
			correctas.
		</li>
	</ul>
</div>`;

// Bloque fijo: puntuación. Siempre este diseño; no editable en backoffice.
const QUESTION_INFO_POINTS_BLOCK = `<div class="d-flex align-items-center justify-content-between w-75 ms-5 mt-3">
	<div class="">Cada prueba justificable:</div>
	<div class="bg-correct p-2" style="width: 145px;">
		<img width="25" src=${handGood} class="me-1" />
		+10 puntos
	</div>
</div>
<div class="d-flex align-items-center justify-content-between w-75 mt-3 ms-5">
	<div class="">Cada prueba injustificada:</div>
	<div class="bg-incorrect p-2" style="width: 145px;">
		<img width="20" src=${handWrong} class="me-2" />
		-20 puntos
	</div>
</div>
</div>`;

function Scenario1Conversation({ intl }) {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const group = useSelector((state) => state.group);
	const { texts, ready } = useGameTexts(group?.game);
	const lastMessageRef = useRef(null);

	const { messages, avatarDoctorUrl, avatarDoctoraUrl, avatarPatientUrl, explorationHtml, historicalTableHtml, questionInfoHtml } = useMemo(() => {
		if (!texts || Object.keys(texts).length === 0) {
			return {
				messages: null,
				avatarDoctorUrl: null,
				avatarDoctoraUrl: null,
				avatarPatientUrl: null,
				explorationHtml: null,
				historicalTableHtml: null,
				questionInfoHtml: null,
			};
		}
		const parsed = parseConversationMessages(texts["scenario1.conversation.messages"]);
		const expl = texts["scenario1.conversation.exploration"];
		const hist = texts["scenario1.conversation.historicalTable"];
		const quest = texts["scenario1.conversation.questionInfo"];
		return {
			messages: parsed,
			avatarDoctorUrl: buildAvatarUrl(texts["avatar.doctor"]),
			avatarDoctoraUrl: buildAvatarUrl(texts["avatar.doctora"]),
			avatarPatientUrl: buildAvatarUrl(texts["avatar.patient"]),
			explorationHtml: expl != null && expl !== "" ? expl : null,
			historicalTableHtml: hist != null && hist !== "" ? hist : null,
			questionInfoHtml: quest != null && quest !== "" ? quest : null,
		};
	}, [texts]);

	const modalAssets = { historicalTable, mano, handGood, handWrong };

	const scrollToBottom = () => {
		lastMessageRef?.current?.scrollIntoView({ behavior: "smooth" });
	};

	const renderChatContent = () => {
		if (messages && messages.length > 0) {
			return (
				<div className="d-flex flex-column conversation-chat">
					{messages.map((msg, index) => (
						<div
							key={index}
							className={`conversation-message ${msg.speaker}`}
							ref={index === messages.length - 1 ? lastMessageRef : null}
						>
							{msg.speaker === "prompt" ? (
								<div className="conversation-prompt">
									{msg.content && msg.content.indexOf("<") >= 0 ? (
										<span dangerouslySetInnerHTML={{ __html: msg.content }} />
									) : (
										msg.content
									)}
								</div>
							) : msg.speaker === "doctor" || msg.speaker === "doctora" ? (
								<>
									<div className="conversation-avatar">
										{(() => {
											const url = (msg.speaker === "doctora" ? avatarDoctoraUrl : null) || avatarDoctorUrl;
											return url ? (
												<img src={url} alt={msg.speaker === "doctora" ? "Doctora" : "Doctor"} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
											) : (
												<div style={{ width: "100%", height: "100%", background: "#e0e8ec", borderRadius: "50%" }} aria-hidden />
											);
										})()}
									</div>
									<div className="conversation-bubble">
										{msg.content && msg.content.indexOf("<") >= 0 ? (
											<span dangerouslySetInnerHTML={{ __html: msg.content }} />
										) : (
											msg.content
										)}
									</div>
								</>
							) : (
								<>
									<div className="conversation-bubble">
										{msg.content && msg.content.indexOf("<") >= 0 ? (
											<span dangerouslySetInnerHTML={{ __html: msg.content }} />
										) : (
											msg.content
										)}
									</div>
									<div className="conversation-avatar">
										{avatarPatientUrl ? (
											<img src={avatarPatientUrl} alt="Paciente" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
										) : (
											<div style={{ width: "100%", height: "100%", background: "#e8e8a0", borderRadius: "50%" }} aria-hidden />
										)}
									</div>
								</>
							)}
						</div>
					))}
				</div>
			);
		}
		if (!ready) {
			return (
				<div className="d-flex align-items-center justify-content-center py-5">
					<div className="txtWhite">Cargando conversación...</div>
				</div>
			);
		}
		return (
			<div className="d-flex align-items-center justify-content-center py-5" ref={lastMessageRef}>
				<div className="txtWhite">No hay mensajes de conversación configurados.</div>
			</div>
		);
	};

	return (
		<div className="h-100">
			<div className="container-fluid bg-internas">
				<div style={{ paddingTop: "6vh", paddingLeft: "6vh" }}>
					<img width={240} src={logoMedical} alt="" />
				</div>
				<div className="row d-flex justify-content-center align-items-center mt-5">
					<div
						className="col-9"
						style={{ height: "60vh", overflowY: "auto", overflowX: "visible", paddingLeft: 8, paddingRight: 8 }}
					>
						{renderChatContent()}
					</div>
				</div>
			</div>
			<div
				className="container"
				style={{
					position: "absolute",
					bottom: "8vh",
				}}
			>
				<div className="d-flex justify-content-end">
					<div
						className="btn-main-cancel me-3"
						onClick={(e) => scrollToBottom()}
					>
						<img height={"40%"} src={reset} />
						<div className="text-btn-main ms-2">
							Ver conservación
						</div>
					</div>
					<div
						className="btn-main"
						onClick={(e) => {
							const explorationContent = explorationHtml || EXPLORATION_HTML;
							modalGeneric({ html: explorationContent }).then(
								(res) => {
									if (res.isConfirmed) {
										let historicalContent = historicalTableHtml
											? injectModalPlaceholders(historicalTableHtml, modalAssets)
											: HISTORICAL_TABLE_HTML;
										historicalContent = repairHistoricoHtml(historicalContent);
										historicalContent = `<div class="historico-modal-content">${historicalContent}</div>`;
										modalGeneric({ html: historicalContent }).then((res) => {
											if (res.isConfirmed) {
												// Solo la parte editable viene del backoffice; el bloque +10/-20 es fijo y diseño original
												let editablePart = questionInfoHtml
													? questionInfoHtml.split(/Cada prueba justificable/i)[0].trim()
													: QUESTION_INFO_EDITABLE;
												if (editablePart && !editablePart.endsWith("</div>")) editablePart += "</div>";
												const questionContent = injectModalPlaceholders(
													(editablePart || QUESTION_INFO_EDITABLE) + QUESTION_INFO_POINTS_BLOCK,
													modalAssets
												);
												modalQuestionInfo({ html: questionContent }).then((res) => {
													if (res.isConfirmed) {
														handleNavigate({
															group: group,
															navigate: navigate,
															dispatch: dispatch,
															navigateTo: ROUTES.SCENARIO1,
															scenarioNum: getScenarioByScreen(ROUTES.SCENARIO1),
														});
													}
												});
											}
										});
									}
								}
							);
						}}
					>
						<div className="text-btn-main">AVANZAR</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default injectIntl(Scenario1Conversation);
